import { AlertTriangleIcon, SearchXIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@app/components/ui/button'
import { TableCell, TableRow } from '@app/components/ui/table'
import { cn } from '@app/lib/cn'

/** Loading skeleton — 32px so the row height matches the table. */
export function SkeletonBar({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('block h-2.5 animate-pulse rounded-xs bg-ink-muted/12', className)}
    />
  )
}

export function SkeletonRows({ rows, cols }: { rows: number; cols: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <TableRow key={rowIndex} aria-hidden>
          {Array.from({ length: cols }, (_, colIndex) => (
            <TableCell key={colIndex}>
              <SkeletonBar className={colIndex === 1 ? 'w-40' : 'w-14'} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

export interface StatusBlockProps {
  title: string
  description?: string
  action?: ReactNode
  tone?: 'empty' | 'error'
}

/** Empty/error states: always shows the next step to take. */
export function StatusBlock({ title, description, action, tone = 'empty' }: StatusBlockProps) {
  const Icon = tone === 'error' ? AlertTriangleIcon : SearchXIcon
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <Icon
        className={cn('size-5', tone === 'error' ? 'text-kordovan' : 'text-ink-muted/60')}
        aria-hidden
      />
      <p className="font-display text-[0.9375rem] font-semibold tracking-tight text-ink">
        {title}
      </p>
      {description ? <p className="max-w-sm text-data text-ink-muted">{description}</p> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  )
}

/**
 * Failed load. `action` replaces the retry button for errors retrying cannot
 * fix — a 404 is the server's final answer, so the model card hands in a
 * "back to the list" link instead of a button that would fail again.
 */
export function ErrorBlock({
  title = 'Veri alınamadı',
  message,
  onRetry,
  action,
}: {
  title?: string
  message: string
  onRetry?: () => void
  action?: ReactNode
}) {
  return (
    <StatusBlock
      tone="error"
      title={title}
      description={message}
      action={
        action ??
        (onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            Yeniden dene
          </Button>
        ) : undefined)
      }
    />
  )
}
